import {
  BatchedMesh,
  BufferGeometry,
  Group,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
} from "three";
import { beginWorkflowPerformanceOperation } from "@models/project/SceneNavigationDiagnosticLogger";
import type { PickingService } from "@runtime/picking/pickingService";

const K_SUPPRESSED_SOURCE_LAYER = 31;
const K_MATRIX_EPSILON = 1e-10;

interface BatchStatisticsEntry {
  readonly geometry: BufferGeometry;
  visible: boolean;
}

const BATCH_STATISTICS = new WeakMap<Object3D, ReadonlyArray<BatchStatisticsEntry>>();

export function isCampfireBatchSource(node: Object3D): boolean {
  return node.userData.campfireBatchSource === true;
}

export function isCampfireBatchOwned(node: Object3D): boolean {
  return node.userData.campfireBatchOwned === true;
}

/**
 * Declares that a custom `ShaderMaterial`'s vertex shader wires three's
 * `batching_pars_vertex` / `batching_vertex` chunks and so may be batched.
 *
 * Three's built-in materials carry those chunks; a hand-written one does not
 * unless it opts in. Batching a shader that ignores `batchingMatrix` draws
 * every instance at the batch root, which is a whole-viewport artifact rather
 * than a subtle one — the XRay ghost did exactly that, stacking a 437-part
 * model onto itself with additive blending until the screen went white. The
 * flag keeps that a per-material opt-in instead of something every future
 * shader has to remember.
 */
export function markSupportsBatchedMesh(material: Material): void {
  material.userData.campfireSupportsBatchedMesh = true;
}

function supportsBatchedMesh(material: Material): boolean {
  if (!(material as Material & { isShaderMaterial?: boolean }).isShaderMaterial) return true;
  return material.userData.campfireSupportsBatchedMesh === true;
}

export function getCampfireBatchStatistics(
  node: Object3D,
): ReadonlyArray<BatchStatisticsEntry> | null {
  return BATCH_STATISTICS.get(node) ?? null;
}

interface SnapshotNodeEntry {
  readonly node: Object3D;
}

interface BatchCandidate {
  readonly source: Mesh;
  readonly geometry: BufferGeometry;
  readonly material: Material;
  readonly matrix: Matrix4;
  readonly ownerSnapshotId: string;
  readonly ancestorSnapshotIds: ReadonlyArray<string>;
}

interface BatchBucket {
  readonly candidates: BatchCandidate[];
  readonly material: Material;
  readonly castShadow: boolean;
  readonly receiveShadow: boolean;
  readonly renderOrder: number;
}

interface BatchRecord {
  readonly source: Mesh;
  readonly geometry: BufferGeometry;
  readonly material: Material;
  readonly batch: BatchedMesh;
  readonly instanceId: number;
  readonly ownerSnapshotId: string;
  readonly ancestorSnapshotIds: ReadonlyArray<string>;
  readonly sourceLayerMask: number;
  readonly statistics: BatchStatisticsEntry;
  visible: boolean;
}

interface OutlineProxy {
  readonly group: Group;
  readonly meshes: ReadonlyArray<{ readonly record: BatchRecord; readonly mesh: Mesh }>;
}

/**
 * Converts compatible static meshes beneath one entity root into material-
 * preserving `BatchedMesh` buckets. The original hierarchy stays authoritative
 * for transforms, bounds, persistence, and tools; source meshes are moved to a
 * camera-excluded layer while their batch instances are rendered.
 */
export class EntityMeshBatcher {
  private readonly m_container = new Group();
  private readonly m_records: BatchRecord[] = [];
  private readonly m_recordsBySnapshot = new Map<string, Set<BatchRecord>>();
  private readonly m_batches: BatchedMesh[] = [];
  private readonly m_dirtySnapshotIds = new Set<string>();
  private readonly m_outlineProxies = new Map<string, OutlineProxy>();
  private readonly m_activeOutlineSnapshotIds = new Set<string>();
  private m_outlineMaterial: MeshBasicMaterial | null = null;
  private m_dirtyAll = false;
  private m_outlineDirty = false;
  private m_disposed = false;

  constructor(
    private readonly m_entityRoot: Object3D,
    private readonly m_batchRoot: Object3D,
    private readonly m_rootSnapshotId: string,
    snapshotNodes: ReadonlyMap<string, SnapshotNodeEntry>,
  ) {
    this.m_container.name = `entity-batches:${m_rootSnapshotId}`;
    this.m_container.userData.campfireBatchOwned = true;
    try {
      this.build(snapshotNodes);
    } catch (error) {
      console.warn(
        `[EntityMeshBatcher] snapshot=${m_rootSnapshotId}: batching failed; using source meshes`,
        error,
      );
      this.dispose();
    }
  }

  get hasBatches(): boolean {
    return this.m_batches.length > 0;
  }

  registerPicking(picker: PickingService): void {
    for (const record of this.m_records) {
      picker.tagBatchedInstance(
        record.batch,
        record.instanceId,
        record.source,
        record.ownerSnapshotId,
      );
    }
  }

  matchesSources(): boolean {
    for (const record of this.m_records) {
      if (record.source.geometry !== record.geometry) return false;
      if (Array.isArray(record.source.material)) return false;
      if (record.source.material !== record.material) return false;
    }
    return true;
  }

  markSnapshotDirty(snapshotId: string): void {
    if (this.m_disposed) return;
    if (snapshotId === this.m_rootSnapshotId) {
      // Batch groups inherit the logical root transform, so a root move is
      // O(1). Only a part-outline proxy (parented at entity level) may need a
      // world-matrix refresh. Root visibility takes the separate
      // `markSnapshotVisibilityDirty` path because it fans out to descendants.
      this.m_outlineDirty = true;
      return;
    }
    if (this.m_recordsBySnapshot.has(snapshotId)) {
      this.m_dirtySnapshotIds.add(snapshotId);
    }
  }

  /**
   * Visibility is different from a root transform: applying the effective
   * hierarchy state changes descendant source nodes too. Refresh every batch
   * record owned by that snapshot even when it is the batch root.
   */
  markSnapshotVisibilityDirty(snapshotId: string): void {
    if (this.m_disposed) return;
    if (snapshotId === this.m_rootSnapshotId) {
      this.m_dirtyAll = true;
    } else if (this.m_recordsBySnapshot.has(snapshotId)) {
      this.m_dirtySnapshotIds.add(snapshotId);
    }
    this.m_outlineDirty = true;
  }

  markAllDirty(): void {
    if (this.m_disposed) return;
    this.m_dirtyAll = true;
    this.m_outlineDirty = true;
  }

  flush(): void {
    if (this.m_disposed) return;
    if (!this.m_dirtyAll && this.m_dirtySnapshotIds.size === 0 && !this.m_outlineDirty) {
      return;
    }

    const dirtyAll = this.m_dirtyAll;
    const records = dirtyAll
      ? this.m_records
      : this.collectDirtyRecords();
    if (records.length > 0) this.syncRecords(records, dirtyAll);
    if (this.m_outlineDirty || records.length > 0) this.syncActiveOutlineProxies();

    this.m_dirtyAll = false;
    this.m_outlineDirty = false;
    this.m_dirtySnapshotIds.clear();
  }

  resolveOutlineObjects(snapshotId: string): ReadonlyArray<Object3D> {
    const records = this.m_recordsBySnapshot.get(snapshotId);
    if (!records || records.size === 0) return [];
    let proxy = this.m_outlineProxies.get(snapshotId);
    if (!proxy) {
      proxy = this.createOutlineProxy(snapshotId, records);
      this.m_outlineProxies.set(snapshotId, proxy);
    }
    return [proxy.group];
  }

  setActiveOutlineSnapshotIds(snapshotIds: ReadonlySet<string>): void {
    this.m_activeOutlineSnapshotIds.clear();
    for (const id of snapshotIds) this.m_activeOutlineSnapshotIds.add(id);
    for (const [id, proxy] of this.m_outlineProxies) {
      proxy.group.visible = this.m_activeOutlineSnapshotIds.has(id);
    }
  }

  dispose(): void {
    if (this.m_disposed) return;
    this.m_disposed = true;

    for (const proxy of this.m_outlineProxies.values()) proxy.group.removeFromParent();
    this.m_outlineProxies.clear();
    this.m_outlineMaterial?.dispose();
    this.m_outlineMaterial = null;

    for (const record of this.m_records) {
      record.source.layers.mask = record.sourceLayerMask;
      delete record.source.userData.campfireBatchSource;
    }
    for (const batch of this.m_batches) {
      BATCH_STATISTICS.delete(batch);
      batch.dispose();
    }
    this.m_container.removeFromParent();
    this.m_records.length = 0;
    this.m_batches.length = 0;
    this.m_recordsBySnapshot.clear();
  }

  private build(snapshotNodes: ReadonlyMap<string, SnapshotNodeEntry>): void {
    this.m_batchRoot.updateWorldMatrix(true, true);
    const batchRootInverse = new Matrix4().copy(this.m_batchRoot.matrixWorld).invert();
    const snapshotIdsByNode = invertSnapshotNodes(snapshotNodes);
    const bucketsByMaterial = new Map<Material, Map<string, BatchBucket>>();

    for (const source of collectSourceMeshes(this.m_batchRoot)) {
      const ownership = resolveSnapshotOwnership(source, this.m_batchRoot, snapshotIdsByNode);
      if (!ownership) continue;
      const candidate = createCandidate(source, batchRootInverse, ownership);
      if (!candidate) continue;

      let materialBuckets = bucketsByMaterial.get(candidate.material);
      if (!materialBuckets) {
        materialBuckets = new Map();
        bucketsByMaterial.set(candidate.material, materialBuckets);
      }
      const key = [
        geometryLayoutKey(candidate.geometry),
        source.castShadow ? "cast" : "no-cast",
        source.receiveShadow ? "receive" : "no-receive",
        source.renderOrder,
      ].join("|");
      let bucket = materialBuckets.get(key);
      if (!bucket) {
        bucket = {
          candidates: [],
          material: candidate.material,
          castShadow: source.castShadow,
          receiveShadow: source.receiveShadow,
          renderOrder: source.renderOrder,
        };
        materialBuckets.set(key, bucket);
      }
      bucket.candidates.push(candidate);
    }

    let bucketIndex = 0;
    for (const materialBuckets of bucketsByMaterial.values()) {
      for (const bucket of materialBuckets.values()) {
        // A one-mesh bucket cannot reduce draw calls and would only duplicate
        // its geometry into a batch buffer.
        if (bucket.candidates.length < 2) continue;
        this.createBatch(bucket, bucketIndex++);
      }
    }

    if (this.m_batches.length > 0) {
      this.m_batchRoot.add(this.m_container);
      this.markAllDirty();
      this.flush();
    }
  }

  private createBatch(bucket: BatchBucket, bucketIndex: number): void {
    const uniqueGeometries = new Set<BufferGeometry>();
    let maxVertexCount = 0;
    let maxIndexCount = 0;
    for (const candidate of bucket.candidates) {
      if (uniqueGeometries.has(candidate.geometry)) continue;
      uniqueGeometries.add(candidate.geometry);
      maxVertexCount += candidate.geometry.getAttribute("position")?.count ?? 0;
      maxIndexCount += candidate.geometry.index?.count ?? 0;
    }
    if (maxVertexCount === 0) return;

    const batch = new BatchedMesh(
      bucket.candidates.length,
      maxVertexCount,
      maxIndexCount,
      bucket.material,
    );
    batch.name = `entity-batch:${this.m_rootSnapshotId}:${bucketIndex}`;
    batch.castShadow = bucket.castShadow;
    batch.receiveShadow = bucket.receiveShadow;
    batch.renderOrder = bucket.renderOrder;
    batch.userData.campfireBatchOwned = true;
    this.m_batches.push(batch);
    this.m_container.add(batch);

    const geometryIds = new Map<BufferGeometry, number>();
    const statistics: BatchStatisticsEntry[] = [];
    BATCH_STATISTICS.set(batch, statistics);
    for (const candidate of bucket.candidates) {
      let geometryId = geometryIds.get(candidate.geometry);
      if (geometryId === undefined) {
        geometryId = batch.addGeometry(candidate.geometry);
        geometryIds.set(candidate.geometry, geometryId);
      }
      const instanceId = batch.addInstance(geometryId);
      batch.setMatrixAt(instanceId, candidate.matrix);

      const visible = isVisibleWithin(candidate.source, this.m_batchRoot);
      batch.setVisibleAt(instanceId, visible);
      const statsEntry: BatchStatisticsEntry = {
        geometry: candidate.geometry,
        visible,
      };
      statistics.push(statsEntry);

      const record: BatchRecord = {
        source: candidate.source,
        geometry: candidate.geometry,
        material: candidate.material,
        batch,
        instanceId,
        ownerSnapshotId: candidate.ownerSnapshotId,
        ancestorSnapshotIds: candidate.ancestorSnapshotIds,
        sourceLayerMask: candidate.source.layers.mask,
        statistics: statsEntry,
        visible,
      };
      this.m_records.push(record);
      for (const snapshotId of record.ancestorSnapshotIds) {
        let records = this.m_recordsBySnapshot.get(snapshotId);
        if (!records) {
          records = new Set();
          this.m_recordsBySnapshot.set(snapshotId, records);
        }
        records.add(record);
      }

      candidate.source.layers.set(K_SUPPRESSED_SOURCE_LAYER);
      candidate.source.userData.campfireBatchSource = true;
    }

  }

  private collectDirtyRecords(): BatchRecord[] {
    const records = new Set<BatchRecord>();
    for (const snapshotId of this.m_dirtySnapshotIds) {
      const snapshotRecords = this.m_recordsBySnapshot.get(snapshotId);
      if (!snapshotRecords) continue;
      for (const record of snapshotRecords) records.add(record);
    }
    return [...records];
  }

  private syncRecords(
    records: ReadonlyArray<BatchRecord>,
    updateAllDescendants: boolean,
  ): void {
    // A partial update must not traverse the complete source hierarchy. Each
    // dirty source refreshes only its ancestor chain; the full recursive walk
    // remains useful for initial/all-dirty synchronization.
    this.m_batchRoot.updateWorldMatrix(true, updateAllDescendants);
    const inverse = new Matrix4().copy(this.m_batchRoot.matrixWorld).invert();
    const matrix = new Matrix4();
    for (const record of records) {
      if (!updateAllDescendants) {
        record.source.updateWorldMatrix(true, false);
      }
      matrix.multiplyMatrices(inverse, record.source.matrixWorld);
      record.batch.setMatrixAt(record.instanceId, matrix);
      const visible = isVisibleWithin(record.source, this.m_batchRoot);
      if (record.visible !== visible) {
        record.visible = visible;
        record.statistics.visible = visible;
        record.batch.setVisibleAt(record.instanceId, visible);
      }
    }
  }

  private createOutlineProxy(
    snapshotId: string,
    records: ReadonlySet<BatchRecord>,
  ): OutlineProxy {
    const endPerformanceOperation = beginWorkflowPerformanceOperation("outline proxy creation", {
      snapshotId,
      meshCount: records.size,
    });
    const group = new Group();
    group.name = `batch-outline-proxy:${snapshotId}`;
    group.visible = this.m_activeOutlineSnapshotIds.has(snapshotId);
    group.userData.campfireBatchOwned = true;

    const material = this.getOutlineMaterial();
    const meshes: Array<{ record: BatchRecord; mesh: Mesh }> = [];
    for (const record of records) {
      const mesh = new Mesh(record.geometry, material);
      mesh.name = `batch-outline-source:${record.source.name}`;
      mesh.matrixAutoUpdate = false;
      mesh.frustumCulled = false;
      mesh.userData.campfireBatchOwned = true;
      group.add(mesh);
      meshes.push({ record, mesh });
    }
    this.m_entityRoot.add(group);
    const proxy = { group, meshes };
    this.syncOutlineProxy(proxy);
    endPerformanceOperation();
    return proxy;
  }

  private getOutlineMaterial(): MeshBasicMaterial {
    if (this.m_outlineMaterial) return this.m_outlineMaterial;
    this.m_outlineMaterial = new MeshBasicMaterial({
      colorWrite: false,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      opacity: 0,
    });
    return this.m_outlineMaterial;
  }

  private syncActiveOutlineProxies(): void {
    for (const [snapshotId, proxy] of this.m_outlineProxies) {
      if (this.m_activeOutlineSnapshotIds.has(snapshotId)) this.syncOutlineProxy(proxy);
    }
  }

  private syncOutlineProxy(proxy: OutlineProxy): void {
    this.m_entityRoot.updateWorldMatrix(true, false);
    const inverse = new Matrix4().copy(this.m_entityRoot.matrixWorld).invert();
    for (const { record, mesh } of proxy.meshes) {
      record.source.updateWorldMatrix(true, false);
      mesh.matrix.multiplyMatrices(inverse, record.source.matrixWorld);
      mesh.matrixWorldNeedsUpdate = true;
      mesh.visible = record.visible;
    }
  }
}

function createCandidate(
  source: Mesh,
  batchRootInverse: Matrix4,
  ownership: {
    readonly ownerSnapshotId: string;
    readonly ancestorSnapshotIds: ReadonlyArray<string>;
  },
): BatchCandidate | null {
  if (isCampfireBatchOwned(source)) return null;
  if ((source as Mesh & { isSkinnedMesh?: boolean }).isSkinnedMesh) return null;
  if (Array.isArray(source.material) || !source.material?.visible) return null;
  if (!supportsBatchedMesh(source.material)) return null;
  if (!source.geometry.getAttribute("position")) return null;
  if (Object.keys(source.geometry.morphAttributes).length > 0) return null;
  if ((source.morphTargetInfluences?.length ?? 0) > 0) return null;

  source.updateWorldMatrix(true, false);
  const matrix = new Matrix4().multiplyMatrices(batchRootInverse, source.matrixWorld);
  if (matrix.determinant() < -K_MATRIX_EPSILON) return null;
  return {
    source,
    geometry: source.geometry,
    material: source.material,
    matrix,
    ownerSnapshotId: ownership.ownerSnapshotId,
    ancestorSnapshotIds: ownership.ancestorSnapshotIds,
  };
}

function collectSourceMeshes(root: Object3D): Mesh[] {
  const meshes: Mesh[] = [];
  const walk = (node: Object3D): void => {
    if (isCampfireBatchOwned(node)) return;
    if ((node as Mesh).isMesh) meshes.push(node as Mesh);
    for (const child of node.children) walk(child);
  };
  walk(root);
  return meshes;
}

function invertSnapshotNodes(
  snapshotNodes: ReadonlyMap<string, SnapshotNodeEntry>,
): ReadonlyMap<Object3D, ReadonlyArray<string>> {
  const idsByNode = new Map<Object3D, string[]>();
  for (const [snapshotId, entry] of snapshotNodes) {
    const ids = idsByNode.get(entry.node) ?? [];
    ids.push(snapshotId);
    idsByNode.set(entry.node, ids);
  }
  return idsByNode;
}

function resolveSnapshotOwnership(
  source: Object3D,
  batchRoot: Object3D,
  snapshotIdsByNode: ReadonlyMap<Object3D, ReadonlyArray<string>>,
): {
  readonly ownerSnapshotId: string;
  readonly ancestorSnapshotIds: ReadonlyArray<string>;
} | null {
  const ancestorSnapshotIds: string[] = [];
  let ownerSnapshotId: string | null = null;
  let current: Object3D | null = source;
  while (current) {
    const ids = snapshotIdsByNode.get(current);
    if (ids) {
      if (!ownerSnapshotId) ownerSnapshotId = ids[ids.length - 1] ?? null;
      for (const id of ids) {
        if (!ancestorSnapshotIds.includes(id)) ancestorSnapshotIds.push(id);
      }
    }
    if (current === batchRoot) break;
    current = current.parent;
  }
  return ownerSnapshotId ? { ownerSnapshotId, ancestorSnapshotIds } : null;
}

function geometryLayoutKey(geometry: BufferGeometry): string {
  const attributes = Object.keys(geometry.attributes)
    .sort()
    .map((name) => {
      const attribute = geometry.getAttribute(name);
      return [
        name,
        attribute.itemSize,
        attribute.normalized ? "normalized" : "raw",
        attribute.array.constructor.name,
      ].join(":");
    });
  attributes.push(geometry.index ? "indexed" : "unindexed");
  return attributes.join(",");
}

function isVisibleWithin(node: Object3D, root: Object3D): boolean {
  let current: Object3D | null = node;
  while (current) {
    // The batch container is parented beneath `root`, so root visibility is
    // already inherited by the whole batch. Per-instance visibility must only
    // encode descendants below it; otherwise a batch built while the entity
    // root is hidden permanently records every instance as hidden, and making
    // that root visible later has nothing left to draw.
    if (current === root) return true;
    if (!current.visible) return false;
    current = current.parent;
  }
  return false;
}
